const THEME = {
  violet: "#6D28D9",
  violetLight: "rgba(109, 40, 217, 0.1)",
  emerald: "#10B981",
  emeraldLight: "rgba(16, 185, 129, 0.1)",
  stone: "#78716C",
  stoneLight: "rgba(120, 113, 108, 0.1)",
  border: "#E7E5E4"
}

function loadChartJs() {
  if (typeof window.Chart !== "undefined") {
    return Promise.resolve()
  }

  if (!window._chartJsLoadPromise) {
    window._chartJsLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js"
      script.onload = resolve
      script.onerror = () => reject(new Error("Chart.js CDN load failed"))
      document.head.appendChild(script)
    })
  }

  return window._chartJsLoadPromise
}

function defaultLineOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 16, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            return `${ctx.dataset.label}: $${Number(ctx.parsed.y).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: THEME.border },
        ticks: {
          font: { size: 11 },
          callback(val) {
            return "$" + Number(val).toLocaleString("en-NZ")
          }
        }
      }
    }
  }
}

export async function createLineChart(canvas, labels, datasets, options = {}) {
  await loadChartJs()

  const ctx = canvas.getContext("2d")

  return new window.Chart(ctx, {
    type: "line",
    data: { labels, datasets },
    options: { ...defaultLineOptions(), ...options }
  })
}

export function destroyChart(chart) {
  if (chart) {
    chart.destroy()
  }
}

export function contributionsAndGrowthDatasets(chartData) {
  return [
    {
      label: chartData.datasets[0].label,
      data: chartData.datasets[0].data,
      borderColor: THEME.stone,
      backgroundColor: THEME.stoneLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    },
    {
      label: chartData.datasets[1].label,
      data: chartData.datasets[1].data,
      borderColor: THEME.violet,
      backgroundColor: THEME.violetLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    }
  ]
}

export const CATEGORY_COLORS = [
  "#6D28D9", // violet — housing
  "#10B981", // emerald — food
  "#3B82F6", // blue — transport
  "#F59E0B", // amber — utilities
  "#EC4899", // pink — entertainment
  "#8B5CF6", // purple — insurance
  "#14B8A6", // teal — healthcare
  "#F97316", // orange — personal
  "#A8A29E"  // stone — remaining
]

function defaultDoughnutOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 12, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
            const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : "0.0"
            const val = "$" + Number(ctx.parsed).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            return `${ctx.label}: ${val} (${pct}%)`
          }
        }
      }
    }
  }
}

function defaultHorizontalBarOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 16, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            return `${ctx.dataset.label}: ${Number(ctx.parsed.x).toFixed(1)}%`
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: THEME.border },
        ticks: {
          font: { size: 11 },
          callback(val) { return val + "%" }
        }
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      }
    }
  }
}

export async function createDoughnutChart(canvas, labels, data, options = {}) {
  await loadChartJs()

  const ctx = canvas.getContext("2d")
  const colors = data.map((_, i) => CATEGORY_COLORS[i % CATEGORY_COLORS.length])

  return new window.Chart(ctx, {
    type: "doughnut",
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: "#FFFFFF"
      }]
    },
    options: { ...defaultDoughnutOptions(), ...options }
  })
}

export async function createHorizontalBarChart(canvas, labels, datasets, options = {}) {
  await loadChartJs()

  const ctx = canvas.getContext("2d")

  return new window.Chart(ctx, {
    type: "bar",
    data: { labels, datasets },
    options: { ...defaultHorizontalBarOptions(), ...options }
  })
}

export function comparisonBarDatasets(chartData) {
  return [
    {
      label: chartData.datasets[0].label,
      data: chartData.datasets[0].data,
      backgroundColor: "rgba(109, 40, 217, 0.7)",
      borderColor: THEME.violet,
      borderWidth: 1,
      borderRadius: 3
    },
    {
      label: chartData.datasets[1].label,
      data: chartData.datasets[1].data,
      backgroundColor: "rgba(168, 162, 158, 0.5)",
      borderColor: THEME.stone,
      borderWidth: 1,
      borderRadius: 3
    }
  ]
}

export function savingsGoalDatasets(chartData) {
  return [
    {
      label: chartData.datasets[0].label,
      data: chartData.datasets[0].data,
      borderColor: THEME.violet,
      backgroundColor: THEME.violetLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    },
    {
      label: chartData.datasets[1].label,
      data: chartData.datasets[1].data,
      borderColor: THEME.emerald,
      backgroundColor: "transparent",
      borderDash: [6, 4],
      fill: false,
      tension: 0,
      pointRadius: 0,
      pointHoverRadius: 0
    }
  ]
}

export function rateHistoryDatasets(label, values) {
  return [
    {
      label,
      data: values,
      borderColor: THEME.violet,
      backgroundColor: THEME.violetLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    }
  ]
}

export function percentLineOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, padding: 16, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            return `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)}%`
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        grid: { color: THEME.border },
        ticks: {
          font: { size: 11 },
          callback(val) {
            return Number(val).toFixed(1) + "%"
          }
        }
      }
    }
  }
}

export { THEME }
